import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Headphones, Mic, PhoneIncoming, Users } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils/cn';
import { useIvrList } from '@/features/ivr/api';
import { useTtsFlowList } from '@/features/tts-flow/api';
import { useLiveInboundCampaigns } from '@/features/inbound-campaigns/api';
import { useAgentGroups } from '@/features/agent-groups/api';

/* ---------- Generic count + recents tile ---------- */

interface CountTileProps {
  count: number;
  loading?: boolean;
  hint?: React.ReactNode;
  ctaHref: string;
  ctaLabel: string;
  emptyLabel?: string;
  recents?: Array<{ id: string | number; label: string; sub?: string }>;
}

function CountTile({
  count,
  loading,
  hint,
  ctaHref,
  ctaLabel,
  emptyLabel,
  recents = [],
}: CountTileProps) {
  if (loading) return <Skeleton className="h-full w-full" />;
  return (
    <div className="flex h-full flex-col gap-2">
      <div className="flex items-baseline gap-2">
        <span className="text-3xl font-semibold tabular-nums">
          {count.toLocaleString()}
        </span>
        {hint && (
          <span className="text-xs text-muted-foreground">{hint}</span>
        )}
      </div>
      {recents.length > 0 ? (
        <ul className="min-h-0 flex-1 space-y-1 overflow-auto">
          {recents.map((r) => (
            <li
              key={r.id}
              className="rounded-md border border-border bg-card px-2 py-1"
            >
              <div className="truncate text-xs font-medium">{r.label}</div>
              {r.sub && (
                <div className="truncate text-[10px] text-muted-foreground">
                  {r.sub}
                </div>
              )}
            </li>
          ))}
        </ul>
      ) : (
        <p className="flex-1 text-xs text-muted-foreground">
          {emptyLabel ?? 'Nothing here yet.'}
        </p>
      )}
      <Button asChild size="sm" variant="outline" className="w-full">
        <Link to={ctaHref}>
          {ctaLabel}
          <ArrowRight className="h-3.5 w-3.5" />
        </Link>
      </Button>
    </div>
  );
}

/* ---------- IVR count ---------- */

export function IvrCountWidget() {
  const q = useIvrList();
  const rows = useMemo(() => q.data ?? [], [q.data]);
  const active = rows.filter((r) => String(r.status ?? '1') === '1').length;
  const recents = useMemo(
    () =>
      [...rows]
        .sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
        .slice(0, 4)
        .map((r) => ({ id: r.id, label: r.flowName, sub: `#${r.id}` })),
    [rows],
  );
  return (
    <CountTile
      count={rows.length}
      loading={q.isLoading}
      hint={
        <>
          <span className="text-emerald-700">{active}</span> active
        </>
      }
      ctaHref="/ivr-list"
      ctaLabel="Open IVR list"
      emptyLabel="No custom IVRs yet."
      recents={recents}
    />
  );
}

/* ---------- TTS Flow count ---------- */

export function TtsFlowCountWidget() {
  const q = useTtsFlowList();
  const rows = useMemo(() => q.data ?? [], [q.data]);
  const active = rows.filter((r) => String(r.status ?? '') === '0').length;
  const recents = useMemo(
    () =>
      [...rows]
        .sort((a, b) => (b.id ?? 0) - (a.id ?? 0))
        .slice(0, 4)
        .map((r) => ({
          id: r.id,
          label: r.ttsFlowName,
          sub: `Template ${r.templateId}`,
        })),
    [rows],
  );
  return (
    <CountTile
      count={rows.length}
      loading={q.isLoading}
      hint={
        <>
          <span className="text-emerald-700">{active}</span> active
        </>
      }
      ctaHref="/tts-flow-list"
      ctaLabel="Open TTS flows"
      emptyLabel="No TTS flows yet."
      recents={recents}
    />
  );
}

/* ---------- Inbound campaign count ---------- */

const NO_FILTER = { campaignName: '', did: '' };

export function InboundCampaignCountWidget() {
  const q = useLiveInboundCampaigns(NO_FILTER);
  const rows = useMemo(() => q.data ?? [], [q.data]);
  const running = rows.filter((r) => r.campaignStatus === 1).length;
  const recents = useMemo(
    () =>
      [...rows]
        .sort((a, b) => b.campaignId - a.campaignId)
        .slice(0, 4)
        .map((r) => ({
          id: r.campaignId,
          label: r.campaignName,
          sub: r.did ?? `Template ${r.templateId}`,
        })),
    [rows],
  );
  return (
    <CountTile
      count={rows.length}
      loading={q.isLoading}
      hint={
        <>
          <span className="text-emerald-700">{running}</span> running
        </>
      }
      ctaHref="/inbound-campaign-list"
      ctaLabel="Open inbound campaigns"
      emptyLabel="No inbound campaigns yet."
      recents={recents}
    />
  );
}

/* ---------- Agent group count ---------- */

export function AgentGroupsCountWidget() {
  const q = useAgentGroups();
  const rows = useMemo(() => q.data ?? [], [q.data]);
  const recents = useMemo(
    () =>
      [...rows]
        .sort((a, b) => b.groupId - a.groupId)
        .slice(0, 4)
        .map((r) => ({ id: r.groupId, label: r.groupName, sub: `#${r.groupId}` })),
    [rows],
  );
  return (
    <CountTile
      count={rows.length}
      loading={q.isLoading}
      hint="Pools of agents"
      ctaHref="/group-agent-list"
      ctaLabel="Open agent groups"
      emptyLabel="No agent groups yet."
      recents={recents}
    />
  );
}

/* ---------- Light visual flair: status pill cluster
 *  for module summary tiles. ---------- */

export function ModuleStatPill({
  icon: Icon,
  label,
  count,
  href,
  tone = 'neutral',
}: {
  icon: LucideIcon;
  label: string;
  count: number;
  href: string;
  tone?: 'neutral' | 'positive' | 'warning';
}) {
  return (
    <Link
      to={href}
      className={cn(
        'flex items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-xs transition-colors hover:border-primary/40',
      )}
    >
      <span
        className={cn(
          'flex h-7 w-7 items-center justify-center rounded-md',
          tone === 'positive' && 'bg-emerald-100 text-emerald-700',
          tone === 'warning' && 'bg-amber-100 text-amber-700',
          tone === 'neutral' && 'bg-primary/10 text-primary',
        )}
      >
        <Icon className="h-3.5 w-3.5" />
      </span>
      <span className="flex-1 truncate font-medium text-foreground">
        {label}
      </span>
      <Badge variant="outline" className="px-1.5 py-0 text-[10px]">
        {count}
      </Badge>
    </Link>
  );
}

// Suppress unused-import warning for icons referenced via prop type alias.
void Headphones;
void Mic;
void PhoneIncoming;
void Users;
